import React, { useEffect } from 'react';
import { FileText, ArrowLeft } from 'lucide-react';
import AppNavBar from './AppNavBar';

const PDFGuidePage = () => {
  // Hide from search engines
  useEffect(() => {
    document.title = "PDF Guide - TeAIch";
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    return () => {
      // Cleanup
      document.head.removeChild(metaRobots);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <AppNavBar currentPage="" />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <FileText className="w-8 h-8 mr-3 text-ink-800" />
          <h1 className="text-3xl font-bold text-ink-800">How to Create a PDF</h1>
        </div>

        {/* Back button */}
        <button 
          onClick={() => window.history.back()}
          className="mb-6 inline-flex items-center bg-coral-300 text-white hover:bg-coral-500 px-4 py-2 rounded transition-colors duration-200"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Mark Work
        </button>
        
        <div className="bg-white rounded-lg border-4 border-ink-900 shadow-lg p-8">
          
          {/* Microsoft Word Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink-800 mb-4 flex items-center">
              <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-lg mr-3">📄</span>
              From Microsoft Word
            </h2>
            
            <div className="bg-blue-50 p-6 rounded-lg border-4 border-ink-900">
              <h3 className="font-bold text-blue-800 mb-3">Quick Steps:</h3>
              <ol className="list-decimal list-inside space-y-2 text-ink-700">
                <li>Open your Word document</li>
                <li>Click <strong>File</strong> in the top menu</li>
                <li>Select <strong>Save As</strong> or <strong>Export</strong></li>
                <li>Choose <strong>PDF</strong> from the file type dropdown</li>
                <li>Click <strong>Save</strong></li>
              </ol>
              
              <div className="mt-4 p-4 bg-white rounded border-l-4 border-blue-500">
                <p className="text-sm text-ink-600">
                  <strong>💡 Pro Tip:</strong> You can also use <strong>Ctrl+P</strong> (Windows) or <strong>Cmd+P</strong> (Mac), 
                  then select "Save as PDF" from the printer options.
                </p>
              </div>
            </div>
          </div>

          {/* Google Docs Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink-800 mb-4 flex items-center">
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-lg mr-3">📗</span>
              From Google Docs
            </h2>
            
            <div className="bg-green-50 p-6 rounded-lg border-4 border-ink-900">
              <h3 className="font-bold text-green-800 mb-3">Quick Steps:</h3>
              <ol className="list-decimal list-inside space-y-2 text-ink-700">
                <li>Open your Google Doc</li>
                <li>Click <strong>File</strong> in the menu</li>
                <li>Select <strong>Download</strong></li>
                <li>Choose <strong>PDF Document (.pdf)</strong></li>
                <li>The PDF will download to your computer</li>
              </ol>
            </div>
          </div>

          {/* PowerPoint Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink-800 mb-4 flex items-center">
              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-lg mr-3">📊</span>
              From PowerPoint
            </h2>
            
            <div className="bg-orange-50 p-6 rounded-lg border-4 border-ink-900">
              <h3 className="font-bold text-orange-800 mb-3">Quick Steps:</h3>
              <ol className="list-decimal list-inside space-y-2 text-ink-700">
                <li>Open your PowerPoint presentation</li>
                <li>Click <strong>File</strong> in the top menu</li>
                <li>Select <strong>Export</strong> or <strong>Save As</strong></li>
                <li>Choose <strong>Create PDF/XPS Document</strong></li>
                <li>Click <strong>Create PDF/XPS</strong></li>
                <li>Choose your save location and click <strong>Publish</strong></li>
              </ol>
            </div>
          </div>

          {/* Mobile Devices Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-ink-800 mb-4 flex items-center">
              <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-lg mr-3">📱</span>
              From Mobile Devices
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-sand-50 p-6 rounded-lg border-4 border-ink-900">
                <h3 className="font-bold text-ink-800 mb-3">iPhone/iPad:</h3>
                <ol className="list-decimal list-inside space-y-2 text-ink-700 text-sm">
                  <li>Open the document in any app</li>
                  <li>Tap the <strong>Share</strong> button</li>
                  <li>Select <strong>Print</strong></li>
                  <li>Pinch to zoom on the preview</li>
                  <li>Tap <strong>Share</strong> again and save as PDF</li>
                </ol>
              </div>
              
              <div className="bg-sand-50 p-6 rounded-lg border-4 border-ink-900">
                <h3 className="font-bold text-ink-800 mb-3">Android:</h3>
                <ol className="list-decimal list-inside space-y-2 text-ink-700 text-sm">
                  <li>Open the document</li>
                  <li>Tap the <strong>three dots menu</strong></li>
                  <li>Select <strong>Print</strong></li>
                  <li>Choose <strong>Save as PDF</strong></li>
                  <li>Tap the <strong>PDF icon</strong> and save</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Why PDF Section */}
          <div className="bg-coral-50 p-6 rounded-lg border-4 border-ink-900">
            <h2 className="text-xl font-bold text-coral-600 mb-4">🎯 Why PDF Works Best</h2>
            <div className="grid md:grid-cols-2 gap-6 text-sm">
              <div>
                <h3 className="font-semibold text-coral-500 mb-2">For AI Analysis:</h3>
                <ul className="space-y-1 text-ink-700">
                  <li>• OCR text extraction works perfectly</li>
                  <li>• Preserves formatting and layout</li>
                  <li>• Consistent across all devices</li>
                  <li>• 10x more accurate feedback</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-coral-500 mb-2">For You:</h3>
                <ul className="space-y-1 text-ink-700">
                  <li>• Universal compatibility</li>
                  <li>• Professional appearance</li>
                  <li>• Smaller file sizes</li>
                  <li>• Easy to share and store</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Return Button */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => window.history.back()}
              className="bg-coral-300 text-white px-8 py-3 rounded-lg hover:bg-coral-500 transition duration-200 inline-flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Mark Work
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PDFGuidePage;